import React, { useCallback, useState } from 'react';
import * as yup from 'yup';
import { useStringsContext } from '../../../../../contexts/strings';
import StoryCard from '../story-card';
import { Story, StoryKeys } from '../../types';
import TextInputWithValidate, {
  TextInputWithValidateProps,
} from '../../../../common/input/text-input-with-validate';
import Stack from '@mui/material/Stack';
import { Typography } from '@mui/material';
import Switch from '@mui/material/Switch';

interface Props
  extends Pick<TextInputWithValidateProps, 'onValue' | 'onValidation' | 'hideValidationError'> {
  selectedStory: Story | null;
}

const captionKey: StoryKeys = 'caption';
const buttonUrlKey: StoryKeys = 'buttonUrl';
const buttonTextKey: StoryKeys = 'buttonText';
const openExternallyKey: StoryKeys = 'openExternally';

const StoryMediaCard = ({ selectedStory, onValue, onValidation, hideValidationError }: Props) => {
  const { strings } = useStringsContext();
  const [linkEnabled, setLinkEnabled] = useState<boolean>(!!selectedStory?.buttonUrl);
  const [openExternally, setOpenExternally] = useState<boolean>(!!selectedStory?.openExternally);

  const onLinkEnabledChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      event.stopPropagation();
      setLinkEnabled(checked);
      if (!checked) {
        // Reset values and any validation errors
        onValue && onValue(buttonUrlKey, null);
        onValue && onValue(buttonTextKey, null);
        onValue && onValue(openExternallyKey, false);
        setOpenExternally(false);
        onValidation && onValidation(buttonUrlKey, true);
        onValidation && onValidation(buttonTextKey, true);
      }
    },
    [onValidation, onValue]
  );

  const onOpenExternallyChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      event.stopPropagation();
      setOpenExternally(checked);
      onValue && onValue(openExternallyKey, checked);
    },
    [onValue]
  );

  return (
    <StoryCard title={strings.stories.detailsTitle}>
      <Stack spacing={4}>
        <TextInputWithValidate
          name={captionKey}
          validation={yup.string().required(strings.validations.isRequired)}
          label={strings.stories.detailsCaptionLabel}
          helperText={strings.stories.detailsCaptionHelperText}
          value={selectedStory?.[captionKey]}
          onValue={onValue}
          onValidation={onValidation}
          hideValidationError={hideValidationError}
        />
        <Stack direction="row" spacing={2} justifyContent="space-between">
          <Typography>{strings.stories.detailsLinkToggle}</Typography>
          <Switch
            size="small"
            checked={linkEnabled}
            onChange={onLinkEnabledChange}
            onClick={(event) => event.stopPropagation()}
          />
        </Stack>
        {linkEnabled && (
          <>
            <TextInputWithValidate
              name={buttonUrlKey}
              validation={yup
                .string()
                .url(strings.validations.validUrl)
                .required(strings.validations.isRequired)}
              label={strings.stories.detailsUrlLabel}
              value={selectedStory?.[buttonUrlKey]}
              onValue={onValue}
              onValidation={onValidation}
              hideValidationError={hideValidationError}
            />
            <TextInputWithValidate
              name={buttonTextKey}
              validation={yup.string().required(strings.validations.isRequired)}
              label={strings.stories.detailsButtonLabel}
              value={selectedStory?.[buttonTextKey]}
              onValue={onValue}
              onValidation={onValidation}
              hideValidationError={hideValidationError}
            />
            <Stack direction="row" spacing={2} justifyContent="space-between">
              <Stack>
                <Typography>{strings.stories.detailsOpenExternallyToggle}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {strings.stories.detailsOpenExternallyHelperText}
                </Typography>
              </Stack>
              <Switch
                size="small"
                checked={openExternally}
                onChange={onOpenExternallyChange}
                onClick={(event) => event.stopPropagation()}
              />
            </Stack>
          </>
        )}
      </Stack>
    </StoryCard>
  );
};

export default StoryMediaCard;
